import {
  inferDirection,
  riskFlowDisplayDirection,
  type RiskFlowAlert,
} from "../../lib/riskflow-feed";
import { bucketOfAlert } from "../../lib/source-buckets";
import { compactTimeAgo } from "../../lib/time-utils";
import { MarketTickerMention } from "../chat/slots/MarketTickerMention";
import { BrandIcon } from "../icon-bank/BrandIcon";
import { useSettings } from "../../contexts/SettingsContext";

interface RiskFlowExpandedFuseHeaderProps {
  alert: RiskFlowAlert;
  impliedPointsLabel: string | null;
}

export function RiskFlowExpandedFuseHeader({
  alert,
  impliedPointsLabel,
}: RiskFlowExpandedFuseHeaderProps) {
  const { preferences } = useSettings();
  const sourceLabel = bucketOfAlert(alert);
  const impliedTicker = tickerSymbol(alert);
  const impliedTone = impliedPointsTone(
    alert,
    preferences.riskflowFilters?.showDirectionalScoring ?? true,
  );
  return (
    <div className="riskflow-expanded-fuse-label mb-1 flex min-w-0 items-center justify-between gap-3 font-mono text-[9px] leading-none tracking-[0.04em] tabular-nums">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[var(--fintheon-text)]/42">
        <span className="inline-flex min-w-0 items-center gap-1 uppercase">
          <BrandIcon
            name={sourceLabel}
            className="h-3 w-3 shrink-0 text-[var(--fintheon-accent)]/55"
            fallback
          />
          <span className="truncate">{sourceLabel}</span>
        </span>
        <span className="normal-case lowercase text-[var(--fintheon-text)]/32">
          {compactTimeAgo(alert.publishedAt)}
        </span>
      </div>
      {impliedPointsLabel && (
        <span
          className="riskflow-decisive-points-trigger shrink-0 text-right"
          data-tone={impliedTone}
        >
          <MarketTickerMention symbol={impliedTicker} showPercent={false}>
            {impliedPointsLabel}
          </MarketTickerMention>
        </span>
      )}
    </div>
  );
}

function tickerSymbol(alert: RiskFlowAlert) {
  const raw = alert.instrument ?? alert.symbols?.[0] ?? "NQ";
  return raw.replace(/^[/$]/, "").toUpperCase();
}

function impliedPointsTone(
  alert: RiskFlowAlert,
  showDirectionalScoring: boolean,
) {
  const direction = riskFlowDisplayDirection(inferDirection(alert), {
    ...alert,
    showDirectionalScoring,
  });
  if (direction === "Bullish") return "bullish";
  if (direction === "Bearish") return "bearish";
  return "neutral";
}
